import type { AxiosError, AxiosResponse } from 'axios';
import axios from 'axios';
import qs from 'qs';

import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { authService } from './auth.service';
import { API_URL } from '@/constants/api-url.constant';
import { clearLanguageSession } from '@/hooks/apply-language-for-session';
import router from '@/router';
import {
  confirmMutationBeforeSend,
  isMutationMethod,
  isProdMutationGuardActive,
} from '@/utils/prod-mutation-guard';

const isLogEnabled = false;

export const httpClient = axios.create({
  timeout: 30000,

  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  maxContentLength: 30 * 1000 * 1000,
});

httpClient.defaults.paramsSerializer = (params) =>
  qs.stringify(params, {
    strictNullHandling: true,
    encodeValuesOnly: true,
  });

const refreshAuthLogic = async () => {
  try {
    await authService.refreshAccessToken();
    return Promise.resolve();
  } catch (err) {
    console.error(err);
    if (router.currentRoute.value.meta.requireAuth) {
      clearLanguageSession();
      await router.push({ name: 'login' });
    }
  }
};

// Every request that fails with 401 while a refresh is running waits for that same refresh
// and is retried afterwards. Pausing the instance instead would reject those concurrent 401s.
// The refresh call itself opts out via `skipAuthRefresh` to avoid an interceptor loop.
createAuthRefreshInterceptor(httpClient, refreshAuthLogic, {
  pauseInstanceWhileRefreshing: false,
});

// Add a request interceptor
httpClient.interceptors.request.use(
  async (config) => {
    config.baseURL = API_URL.value;

    const isAuthRequest = config.url?.includes('/auth/');
    if (isProdMutationGuardActive() && isMutationMethod(config.method) && !isAuthRequest) {
      const result = await confirmMutationBeforeSend();
      if (result === 'cancel') {
        throw Object.assign(new Error('Mutation cancelled by user'), {
          __handled: true,
          code: 'PROD_MUTATION_CANCELLED',
        });
      }
    }

    if (isLogEnabled) {
      console.log('Network Request:', `${config.baseURL}${config.url}`, config.method);
    }
    return config;
  },
  async (error) => {
    if (isLogEnabled) console.error('Network Request:', error);
    throw error;
  },
);

// Add a response interceptor
httpClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
  },
  async (error: AxiosError) => {
    if (isLogEnabled) console.error('Network Response:', error);

    throw error;
  },
);
